/**
 * MODULE SUIVI DE STAGE — Tuteurs → Paramétrage → CFA : la TRANSMISSION AU CFA (lot 3.5d-2, E148, E165)
 * ======================================================================================================
 * D'après la maquette (`#tparam-cfa` → `#cfaTransmission`), sous la carte « Suivi des apprentis » et SEULEMENT
 * en mode « Par le CFA » (E149) :
 *   1. CFA destinataire : par défaut + une ligne par formation en alternance de l'année (vide = par défaut ;
 *      deux CFA = deux messages) ;
 *   2. Colonnes transmises : glisser pour réordonner, cocher (Nom, Prénom, Tuteur obligatoires), aperçu sur deux
 *      vrais alternants de l'année ;
 *   3. Courriel du CFA : le MÊME éditeur que les autres courriels (versions, rétablir), avec « Joindre le fichier Excel ».
 * Chaque carte s'enregistre seule ; une erreur s'affiche DANS sa carte (le serveur nomme ce qui manque).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { stageAPI } from '../services/api';
import { AccordeonMail } from './StageParametrageContacts';
import { COURRIEL_CFA } from '../services/modelesCourrielPresentation';

const LECTURE_SEULE = 'Lecture seule : votre profil ne permet pas de modifier ce module.';
const message = (e, defaut) => e.response?.data?.detail || e.response?.data?.error || defaut;

// Lot MC (2026-10-28) : la description vit dans services/modelesCourrielPresentation.js (lue aussi par les fenêtres d'envoi).

function CarteErreur({ texte }) {
    if (!texte) return null;
    return <div className="stg-encart stg-encart-erreur" style={{ margin: '0 0 10px' }}><i className="fa-solid fa-circle-exclamation" /><span>{texte}</span></div>;
}

export default function StageParametrageCfa({ departementId, anneeId, modifiable, onSucces }) {
    const [donnees, setDonnees] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [cfa, setCfa] = useState({ nom: '', email: '' });
    const [formations, setFormations] = useState([]);
    const [colonnes, setColonnes] = useState([]);
    const [erreurs, setErreurs] = useState({});           // par carte : cfa | colonnes
    const [enCours, setEnCours] = useState(null);          // la carte qui enregistre
    const [glisse, setGlisse] = useState(null);            // la clé de la colonne glissée
    const [modeles, setModeles] = useState(null);
    const [ouvert, setOuvert] = useState('cfa_transmission');

    /** Prend la réponse du serveur. `carte` : seule la carte enregistrée repart du serveur — une saisie en cours
     *  dans l'AUTRE carte n'est pas effacée. */
    const prendre = useCallback((d, carte = null) => {
        setDonnees((avant) => (carte && avant ? { ...avant, ...d } : d));
        if (!carte || carte === 'cfa') {
            setCfa({ nom: d.cfa_defaut?.nom || '', email: d.cfa_defaut?.email || '' });
            setFormations((d.formations || []).map((f) => ({ ...f })));
        }
        if (!carte || carte === 'colonnes') setColonnes((d.colonnes || []).map((c) => ({ ...c })));
        if (d.modeles_mail) setModeles(d.modeles_mail);
    }, []);

    useEffect(() => {
        let vivant = true;
        setErreur(null);
        stageAPI.getReglagesCfa(departementId, anneeId)
            .then((r) => { if (vivant) prendre(r.data); })
            .catch((e) => { if (vivant) setErreur(message(e, 'Lecture des réglages du CFA impossible')); });
        return () => { vivant = false; };
    }, [departementId, anneeId, prendre]);

    const enregistrer = async (carte, corps, succes) => {
        setEnCours(carte); setErreurs((x) => ({ ...x, [carte]: null }));
        try {
            const r = await stageAPI.enregistrerReglagesCfa(departementId, anneeId, corps);
            const { modeles_mail, ...reste } = r.data;   // l'écriture des réglages ne relit pas le modèle
            prendre(reste, carte);
            if (onSucces) onSucces(succes);
        } catch (e) {
            setErreurs((x) => ({ ...x, [carte]: message(e, "L'enregistrement n'a pas abouti") }));
        } finally { setEnCours(null); }
    };

    // ── Colonnes : glisser pour réordonner (la maquette : poignée ⋮⋮, toute la ligne se saisit)
    const deplacer = (cle, vers) => {
        if (!cle || cle === vers) return;
        setColonnes((liste) => {
            const de = liste.findIndex((c) => c.cle === cle), a = liste.findIndex((c) => c.cle === vers);
            if (de < 0 || a < 0) return liste;
            const copie = [...liste];
            const [x] = copie.splice(de, 1);
            copie.splice(a, 0, x);
            return copie;
        });
    };
    const actives = useMemo(() => colonnes.filter((c) => c.actif), [colonnes]);
    const colonnesModifiees = donnees && JSON.stringify(colonnes.map((c) => [c.cle, c.actif])) !== JSON.stringify((donnees.colonnes || []).map((c) => [c.cle, c.actif]));
    const cfaModifie = donnees && (cfa.nom !== (donnees.cfa_defaut?.nom || '') || cfa.email !== (donnees.cfa_defaut?.email || '')
        || JSON.stringify(formations.map((f) => [f.nom || '', f.email || ''])) !== JSON.stringify((donnees.formations || []).map((f) => [f.nom || '', f.email || ''])));

    if (erreur) return <div className="stg-encart stg-encart-erreur" style={{ marginTop: 14 }}><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>;
    if (!donnees) return <div className="stg-section-avenir" style={{ marginTop: 14 }}><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture des réglages du CFA…</div>;
    const verrou = !modifiable;

    return (
        <div className="stg-cfa-transmission" style={{ marginTop: 14 }}>
            {/* ── 1. CFA destinataire ── */}
            <div className="card" style={{ marginBottom: 14 }} data-carte="cfa">
                <div className="card-header">
                    <span><i className="fa-solid fa-building-columns" /> CFA destinataire</span>
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>À qui part la transmission des tuteurs des alternants</span>
                </div>
                <div style={{ padding: '12px 16px', fontSize: 12 }}>
                    <CarteErreur texte={erreurs.cfa} />
                    <div className="stg-cfa-grille">
                        <span style={{ fontWeight: 600 }}>Par défaut</span>
                        <input className="input-sm" value={cfa.nom} placeholder="Nom du CFA" disabled={verrou || !!enCours} aria-label="Nom du CFA par défaut"
                               onChange={(e) => setCfa({ ...cfa, nom: e.target.value })} />
                        <input className="input-sm" value={cfa.email} placeholder="adresse@cfa.fr" disabled={verrou || !!enCours} aria-label="Adresse du CFA par défaut"
                               onChange={(e) => setCfa({ ...cfa, email: e.target.value })} />
                        {formations.map((f, i) => (
                            <React.Fragment key={f.sous_niveau_id}>
                                <span title={f.nb_alternants ? `${f.nb_alternants} alternant${f.nb_alternants > 1 ? 's' : ''} cette année` : 'Aucun alternant cette année'}>{f.formation}</span>
                                <input className="input-sm" value={f.nom || ''} placeholder="(comme par défaut)" disabled={verrou || !!enCours} aria-label={`Nom du CFA — ${f.formation}`}
                                       onChange={(e) => setFormations((l) => l.map((x, j) => (j === i ? { ...x, nom: e.target.value } : x)))} />
                                <input className="input-sm" value={f.email || ''} placeholder="(comme par défaut)" disabled={verrou || !!enCours} aria-label={`Adresse du CFA — ${f.formation}`}
                                       onChange={(e) => setFormations((l) => l.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} />
                            </React.Fragment>
                        ))}
                    </div>
                    {!formations.length && <div style={{ marginTop: 6, color: '#64748b', fontSize: 11 }}>Aucune formation en alternance cette année : seul le CFA par défaut s'applique.</div>}
                    <div style={{ marginTop: 8, color: '#64748b', fontSize: 11 }}>
                        Une ligne par formation en alternance : laissez vide pour utiliser le CFA par défaut. Si deux formations dépendent de deux CFA différents, la transmission prépare <b>un message par CFA</b>.
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                        <button type="button" className="btn btn-green btn-sm" disabled={verrou || !!enCours || !cfaModifie} title={verrou ? LECTURE_SEULE : (!cfaModifie ? 'Rien à enregistrer' : undefined)}
                                onClick={() => enregistrer('cfa', {
                                    cfa_defaut: { nom: cfa.nom.trim(), email: cfa.email.trim() },
                                    formations: formations.map((f) => ({ sous_niveau_id: f.sous_niveau_id, nom: (f.nom || '').trim(), email: (f.email || '').trim() }))
                                }, 'CFA destinataire enregistré.')}>
                            <i className={'fa-solid ' + (enCours === 'cfa' ? 'fa-circle-notch stg-tourne' : 'fa-check')} /> Enregistrer
                        </button>
                    </div>
                </div>
            </div>

            {/* ── 2. Colonnes transmises ── */}
            <div className="card" style={{ marginBottom: 14 }} data-carte="colonnes">
                <div className="card-header">
                    <span><i className="fa-solid fa-table-columns" /> Colonnes transmises</span>
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Les mêmes pour le tableau du message et le fichier Excel · glissez pour réordonner</span>
                </div>
                <div className="stg-cfa-colonnes-corps">
                    <div className="stg-cfa-cols">
                        <CarteErreur texte={erreurs.colonnes} />
                        {colonnes.map((c) => (
                            <label key={c.cle} className={'stg-cfa-col' + (c.obligatoire ? ' fixe' : '') + (glisse === c.cle ? ' glisse' : '')} data-cle={c.cle}
                                   draggable={!verrou && !enCours}
                                   onDragStart={(e) => { setGlisse(c.cle); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', c.cle); } catch (_) { /* */ } }}
                                   onDragOver={(e) => { if (glisse) { e.preventDefault(); deplacer(glisse, c.cle); } }}
                                   onDrop={(e) => e.preventDefault()}
                                   onDragEnd={() => setGlisse(null)}>
                                <i className="fa-solid fa-grip-vertical" />
                                <input type="checkbox" checked={c.actif} disabled={c.obligatoire || verrou || !!enCours}
                                       onChange={(e) => setColonnes((l) => l.map((x) => (x.cle === c.cle ? { ...x, actif: e.target.checked } : x)))} />
                                {c.libelle}
                                {c.obligatoire && <span>obligatoire</span>}
                            </label>
                        ))}
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}><i className="fa-solid fa-shield-halved" /> Ne cochez que ce dont le CFA a besoin : ces données partent chez un tiers.</div>
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div className="stg-cfa-apercu-titre">Aperçu</div>
                        {donnees.apercu?.length ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="stg-cfa-apercu">
                                    <thead><tr>{actives.map((c) => <th key={c.cle}>{c.libelle}</th>)}</tr></thead>
                                    <tbody>{donnees.apercu.map((l, i) => <tr key={i}>{actives.map((c) => <td key={c.cle}>{l[c.cle] || '—'}</td>)}</tr>)}</tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                                Aucun alternant de l'année n'a encore de tuteur : l'aperçu apparaîtra dès la première affectation.
                                <div style={{ marginTop: 6 }}>Colonnes transmises : {actives.map((c) => c.libelle).join(' · ')}</div>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                            <button type="button" className="btn btn-green btn-sm" disabled={verrou || !!enCours || !colonnesModifiees} title={verrou ? LECTURE_SEULE : (!colonnesModifiees ? 'Rien à enregistrer' : undefined)}
                                    onClick={() => enregistrer('colonnes', { colonnes: colonnes.map((c) => ({ cle: c.cle, actif: c.actif })) }, 'Colonnes transmises enregistrées.')}>
                                <i className={'fa-solid ' + (enCours === 'colonnes' ? 'fa-circle-notch stg-tourne' : 'fa-check')} /> Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── 3. Courriel du CFA ── */}
            <div className="card" data-carte="courriel">
                <div className="card-header">
                    <span><i className="fa-solid fa-envelope" /> Courriel du CFA</span>
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Même éditeur et mêmes versions que les autres courriels</span>
                </div>
                <div style={{ padding: '12px 16px' }}>
                    {!modeles ? <div className="stg-section-avenir"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture du courriel…</div>
                        : modeles.map((m) => (
                            <AccordeonMail key={m.type_mail} modele={m} ouvert={ouvert === m.type_mail}
                                onBasculer={() => setOuvert((o) => (o === m.type_mail ? null : m.type_mail))}
                                departementId={departementId} modifiable={modifiable}
                                onMiseAJour={(r) => { if (r?.modeles_mail) setModeles(r.modeles_mail); }}
                                {...COURRIEL_CFA} />
                        ))}
                </div>
            </div>
        </div>
    );
}
